import React, { useContext } from 'react';
import { FieldProps } from 'formik';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { Box } from '@mui/material';
import { IllustrationDragAndDrop, IconCheckCircle } from '@maiia/design-system';
import Text from '../../atoms/Text/Text';
import { useTranslation } from '@/src/i18n';
import OpenTokContext from '../TlcSession/context';
import DocumentButton from '@/components/molecules/DocumentButton/DocumentButton';
import { ACCEPT_FILE_PATTERN, TLC_UPLOADING_DOCUMENT } from '@/src/constants';

const signals = {
  documentClosed: {
    name: TLC_UPLOADING_DOCUMENT,
    value: false,
    pro: false,
  },
  documentOpened: {
    name: TLC_UPLOADING_DOCUMENT,
    value: true,
    pro: false,
  },
};

const File: React.FC<FieldProps> = ({
  field,
  form: { setFieldValue, errors, touched, setFieldTouched },
}) => {
  const { t } = useTranslation();
  const { session } = useContext(OpenTokContext);

  const onDrop = React.useCallback(acceptedFiles => {
    const dropFile = acceptedFiles?.[0];
    setFieldTouched(field.name, true, false);
    setFieldValue(field.name, dropFile);
    session.current?.session?.signal({
      data: JSON.stringify(signals.documentClosed),
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ACCEPT_FILE_PATTERN,
    multiple: false,
    onFileDialogCancel: () => {
      session.current?.session?.signal({
        data: JSON.stringify(signals.documentClosed),
      });
    },
  });

  const dropZoneInputProps = getInputProps() ?? {};

  return (
    <div
      {...getRootProps()}
      className={clsx(
        'upload-zone',
        field.value && !errors?.[field.name] && 'upload-zone--success',
      )}
      data-testid="upload-zone"
    >
      <input
        {...dropZoneInputProps}
        onClick={e => {
          session.current?.session?.signal({
            data: JSON.stringify(signals.documentOpened),
          });
          dropZoneInputProps.onClick?.(e);
        }}
      />
      <Box display="flex" flexDirection="column" justifyContent="center">
        <DocumentButton data-testid="upload-btn" className="upload-btn">
          {field.value ? <IconCheckCircle /> : <IllustrationDragAndDrop />}
        </DocumentButton>
      </Box>
      {field.value ? (
        <div className="text">
          <p>{field.value.name}</p>
        </div>
      ) : (
        <div className="text">
          <p>{t('drop_your_files')}</p>
          <p className="light">{t('document_types_supported')}</p>
        </div>
      )}
      {touched[field.name] && errors[field.name] && (
        <div className="text">
          <Text component="p" className="error">
            {t(errors[field.name] as string)}
          </Text>
        </div>
      )}
    </div>
  );
};

export default File;
